import {
  IconPlugConnected,
  IconCalendar,
  IconToggleLeft,
  IconX,
  IconRefresh,
} from "@tabler/icons-react";
import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  ListAllIntegrations,
  SetupGoogleCalendarIntegration,
  RemoveGoogleCalendarIntegration,
} from "../../../services/IntegrationsServices";
import Swal from "sweetalert2";

const Integrations = ({
  hubMode = false,
  agentId = null,
  agentContext = null,
}) => {
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [googleCalendarAgentStatus, setGoogleCalendarAgentStatus] = useState(null);

  // Função utilitária para extrair mensagem de erro da API
  const extractErrorMessage = (error, defaultMessage) => {

    if (error.response && error.response.data) {
      // Se a API retorna um objeto com campo 'error'
      if (error.response.data.error) {
        return error.response.data.error;
      }
      // Se a API retorna um objeto com campo 'message'
      else if (error.response.data.message) {
        return error.response.data.message;
      }
      // Se a API retorna uma string diretamente
      else if (typeof error.response.data === "string") {
        return error.response.data;
      }
    }
    // Se o erro tem uma mensagem direta
    else if (error.message) {
      return error.message;
    }

    return defaultMessage;
  };

  // Carrega as integrações disponíveis
  const loadIntegrations = useCallback(async () => {
    try {
      setLoadingIntegrations(true);

      // Incluir agent_id se estivermos dentro do AgentHub
      const params = agentId ? { agent_id: agentId } : {};
      const response = await ListAllIntegrations(params);

      // Armazenar google_calendar_agent_status globalmente
      setGoogleCalendarAgentStatus(response.google_calendar_agent_status);
      
      // Filtrar apenas integrações do tipo google_calendar e adicionar google_calendar_agent_status
      const googleCalendarIntegrations = (response.integrations || [])
        .filter((integration) => integration.integration_type === "google_calendar")
        .map((integration) => ({
          ...integration,
          google_calendar_agent_status: response.google_calendar_agent_status
        }));
      
      setAvailableIntegrations(googleCalendarIntegrations);
    } catch (error) {
      console.error("Erro ao carregar integrações:", error);
      setAvailableIntegrations([]);
    } finally {
      setLoadingIntegrations(false);
    }
  }, [agentId]);

  // Carrega integrações quando o componente monta
  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // Handler para integrar com Google Calendar
  const handleIntegrateGoogleCalendar = async (integration) => {
    try {
      const response = await SetupGoogleCalendarIntegration(agentId, {
        agent_id: agentId,
        integration_id: integration.id,
        integration_type: "google_calendar",
      });

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Integração Configurada!",
          text: "Google Calendar integrado com sucesso!",
          timer: 3000,
          showConfirmButton: false,
        });

        // Recarregar integrações
        await loadIntegrations();
      } else {
        throw new Error(response.message || "Erro ao configurar integração");
      }
    } catch (error) {
      console.error("Erro ao integrar Google Calendar:", error);

      const errorMessage = extractErrorMessage(
        error,
        "Não foi possível configurar a integração."
      );

      Swal.fire({
        icon: "error",
        title: "Erro na Integração",
        text: errorMessage,
      });
    }
  };

  // Handler para pausar integração
  const handlePauseIntegration = async (integration) => {
    const result = await Swal.fire({
      title: "Pausar Integração?",
      text: `Deseja pausar a integração ${integration.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, pausar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      // Aqui você implementaria a API para pausar a integração
      Swal.fire({
        icon: "success",
        title: "Integração Pausada!",
        timer: 2000,
        showConfirmButton: false,
      });

      await loadIntegrations();
    } catch (error) {
      console.error("Erro ao pausar integração:", error);

      const errorMessage = extractErrorMessage(
        error,
        "Não foi possível pausar a integração."
      );

      Swal.fire({
        icon: "error",
        title: "Erro ao Pausar Integração",
        text: errorMessage,
      });
    }
  };

  // Handler para remover integração
  const handleRemoveIntegration = async (integration) => {
    const result = await Swal.fire({
      title: "Remover Integração?",
      text: `Deseja remover a integração ${integration.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      await RemoveGoogleCalendarIntegration(agentId);

      Swal.fire({
        icon: "success",
        title: "Integração Removida!",
        text: "A integração foi removida com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      await loadIntegrations();
    } catch (error) {
      console.error("Erro ao remover integração:", error);
      if (error.response.status === 404) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text:
            error.response?.data?.error ||
            "Não foi possível remover a integração.",
        });
      } else {
        const errorMessage = extractErrorMessage(
          error,
          "Não foi possível remover a integração."
        );

        Swal.fire({
          icon: "error",
          title: "Erro ao Remover Integração",
          text: errorMessage,
        });
      }
    }
  };

  // Renderização condicional do header
  const renderHeader = () => {
    if (hubMode) {
      return (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <IconPlugConnected className="me-3 text-primary" size={24} />
            <div>
              <h4 className="mb-0">Integrações</h4>
              {agentContext && (
                <p className="text-muted mb-0">Integrações disponíveis</p>
              )}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadIntegrations}
              disabled={loadingIntegrations}
            >
              <IconRefresh
                size={16}
                className={loadingIntegrations ? "spinner-border" : ""}
              />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <IconPlugConnected className="me-3 text-primary" size={32} />
          <div>
            <h2 className="mb-0">Integrações do Agente</h2>
            <p className="text-muted mb-0">
              Conecte seu agente com sistemas externos
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={hubMode ? "p-4" : "container-fluid py-4"}>
      {renderHeader()}

      {/* Informações do Status do Agente */}
      {googleCalendarAgentStatus && (
        <Alert variant="info" className="mb-4">
          <div className="d-flex align-items-center">
            <IconCalendar size={20} className="me-2" />
            <div>
              <strong>Status do Agente:</strong>{" "}
              {googleCalendarAgentStatus.agent_found ? (
                <>
                  <span className="text-success">
                    {googleCalendarAgentStatus.agent_name}
                  </span>
                  {" - "}
                  <span className={googleCalendarAgentStatus.google_calendar_enabled ? "text-success" : "text-warning"}>
                    {googleCalendarAgentStatus.google_calendar_enabled 
                      ? "Google Calendar habilitado" 
                      : "Google Calendar não habilitado"}
                  </span>
                </>
              ) : (
                <span className="text-danger">Agente não encontrado</span>
              )}
            </div>
          </div>
        </Alert>
      )}

      {loadingIntegrations ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="primary" />
          <span className="ms-2 text-muted">Carregando integrações...</span>
        </div>
      ) : availableIntegrations.length > 0 ? (
        <div className="integration-cards">
          {availableIntegrations.map((integration) => (
            <Card
              key={integration.id}
              className="mb-3 border integration-card-hover"
            >
              <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="integration-logo me-3">
                      <IconCalendar size={40} className="text-primary" />
                    </div>
                    <div>
                      <h6 className="mb-1">{integration.name}</h6>
                      <small className="text-muted">
                        <b>Google Calendar</b>
                      </small>
                      <div className="mt-1">
                        <Badge
                          bg={
                            integration?.google_calendar_agent_status
                              ?.google_calendar_enabled
                              ? "success"
                              : "secondary"
                          }
                          className="small"
                        >
                          {integration?.google_calendar_agent_status?.google_calendar_enabled
                            ? "Integrado ao Agente"
                            : "Não Integrado"
                          }
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Integrar com Google Agenda</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="success"
                        className="d-flex align-items-center justify-content-center p-0"
                        onClick={() =>
                          handleIntegrateGoogleCalendar(integration)
                        }
                        style={{ width: "40px", height: "40px" }}
                      >
                        <IconPlugConnected size={18} />
                      </Button>
                    </OverlayTrigger>
                
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Remover integração</Tooltip>}
                    >
                      <Button
                        size="sm"
                        variant="danger"
                        className="d-flex align-items-center justify-content-center p-0"
                        onClick={() => handleRemoveIntegration(integration)}
                        style={{ width: "40px", height: "40px" }}
                      >
                        <IconX size={18} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-5">
          <IconCalendar size={64} className="text-muted mb-3" />
          <h5 className="text-muted">Nenhuma integração disponível</h5>
          <p className="text-muted">
            Configure uma integração do Google Agenda primeiro na seção de
            Integrações.
          </p>
          <Button
            variant="outline-primary"
            onClick={() => {
              window.location.href = "/channels";
            }}
          >
            Ir para Integrações
          </Button>
        </div>
      )}

      <div className="mt-4">
        <Alert variant="light" className="text-center">
          <small className="text-muted">
            Mais tipos de integração serão adicionados em breve
          </small>
        </Alert>
      </div>

      {/* Styles */}
      <style jsx>{`
        .integration-card-hover {
          transition: all 0.3s ease;
          border-radius: 12px;
        }

        .integration-card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border-color: var(--primary) !important;
        }
      `}</style>
    </div>
  );
};

export default Integrations;
